// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/SponsorDropdown.js

import React, { useEffect, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { API_URLS, insertSponsor, updateSponsor } from '../api/api';
import ToastNotification from './ToastNotification';

const SponsorDropdown = ({
  sponsors,
  setSponsors,
  selectedSponsor,
  setSelectedSponsor,
  translations,
  branchId,
  memberData,
  mode,
  pendingSponsorId,
  setPendingSponsorId,
  tempUserId
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localSelectedSponsor, setLocalSelectedSponsor] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [sponsorViewData, setSponsorViewData] = useState(null);

  const prepareSponsorOptions = (viewData, allData) => {
    let options = [];

    if (viewData && viewData.length > 0) {
      viewData.forEach(s => {
        options.push({
          label: `${s.sponsor_first_name} ${s.sponsor_sur_name} (ID: ${s.sponsor_id})`,
          value: s.sponsor_id,
          source: 'SPONSOR_VIEW'
        });
      });
    }
    if (allData && allData.length > 0) {
      allData.forEach(u => {
        if (!options.some(opt => opt.value === u.user_ID)) {
          options.push({
            label: `${u.first_name} ${u.sur_name} (ID: ${u.user_ID})`,
            value: u.user_ID,
            source: 'ALL_SPONSORS'
          });
        }
      });
    }
    return options;
  };

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const localUserId = parseInt(localStorage.getItem('user_ID')) || null;

        let sponsorView = null;
        let allSponsorsData = null;

        if (mode === 'edit' && memberData?.user_ID) {
          try {
            const resp = await fetch(API_URLS.SPONSOR_VIEW(memberData.user_ID), {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (resp.ok) {
              sponsorView = await resp.json();
              setSponsorViewData(sponsorView);
            } else {
              console.error('Error fetching SPONSOR_VIEW:', resp.status);
            }
          } catch (err) {
            console.error('Error fetching SPONSOR_VIEW:', err);
          }
        }

        try {
          const respAll = await fetch(API_URLS.ALL_SPONSORS(branchId, localUserId), {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (respAll.ok) {
            allSponsorsData = await respAll.json();
          } else {
            console.error('Error fetching ALL_SPONSORS:', respAll.status);
          }
        } catch (err) {
          console.error('Error fetching ALL_SPONSORS:', err);
        }

        let sponsorOptions = prepareSponsorOptions(sponsorView, allSponsorsData);

        if (mode === 'edit' && sponsorView && sponsorView.length > 0) {
          const defaultSponsorId = sponsorView[0].sponsor_id;
          setSelectedSponsor(defaultSponsorId);
          setLocalSelectedSponsor(defaultSponsorId);
        } else {
          setSelectedSponsor(null);
          setLocalSelectedSponsor(null);
        }

        if (sponsorOptions.length === 0) {
          sponsorOptions = [{ label: 'No Sponsors Available', value: null }];
        }
        setSponsors(sponsorOptions);
      } catch (error) {
        console.error('Error in fetchSponsors:', error);
        setSponsors([{ label: 'No Sponsors Available', value: null }]);
        setSelectedSponsor(null);
        setLocalSelectedSponsor(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsors();
  }, [branchId, memberData?.user_ID, mode, setSponsors, setSelectedSponsor]);

  // For edit mode – real update/insert; for add mode, just store pendingSponsorId
  const performInsertSponsor = async (newSponsorId) => {
    if (!memberData || !memberData.user_ID) {
      console.error("Missing user_ID for insertSponsor in edit mode.");
      setToastMessage('Missing user ID for inserting sponsor');
      setToastVariant('error');
      setShowToast(true);
      return;
    }
    const insertData = {
      user_id: memberData.user_ID,
      sponsor_id: newSponsorId,
      team_id: "A1",
      position: 1,
      commission_rate: 2.02
    };

    try {
      setIsSubmitting(true);
      await insertSponsor(insertData);
      setToastMessage('Sponsor inserted successfully!');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Insert sponsor error:', error);
      setToastMessage(`Error: ${error.message}`);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const performUpdateSponsor = async (newSponsorId) => {
    if (!sponsorViewData || sponsorViewData.length === 0 || !sponsorViewData[0].user_team_id) {
      return performInsertSponsor(newSponsorId);
    }

    const updateData = {
      user_team_id: sponsorViewData[0].user_team_id,
      new_team_id: "B1",
      new_sponsor_id: newSponsorId,
      new_position: 8,
      new_commission_rate: 6.92
    };

    try {
      setIsSubmitting(true);
      await updateSponsor(updateData);
      setToastMessage('Sponsor updated successfully!');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Update sponsor error:', error);
      setToastMessage(`Error: ${error.message}`);
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSponsorChange = (e) => {
    const newSponsorId = e.value;
    setLocalSelectedSponsor(newSponsorId);

    if (!newSponsorId) {
      setSelectedSponsor(null);
      setPendingSponsorId(null);
      return;
    }

    if (mode === 'add') {
      setPendingSponsorId(newSponsorId);
      console.log(`[Add mode] Sponsor selected => pendingSponsorId = ${newSponsorId}, tempUserId = ${tempUserId}`);
    } else if (mode === 'edit') {
      setSelectedSponsor(newSponsorId);
      performUpdateSponsor(newSponsorId);
    }
  };

  return (
    <div className="mb-3">
      <label htmlFor="sponsorDropdown" className="form-label">
        {translations.sponsorLabel || "Sponsor"}
      </label>
      <Dropdown
        value={localSelectedSponsor}
        options={isLoading ? [] : sponsors}
        onChange={handleSponsorChange}
        optionLabel="label"
        placeholder={translations.sponsorPlaceholder || "Select a Sponsor"}
        filter
        showClear
        filterBy="label"
        className="w-100"
        disabled={isLoading || isSubmitting}
      />
      {isSubmitting && <ProgressSpinner style={{ width: '20px', height: '20px' }} />}
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />
    </div>
  );
};

export default SponsorDropdown;
